/**
 * Comprehensive validation utilities for Lasmari Chauffeurs
 * Provides security-focused validation for user inputs
 */

// Email validation with comprehensive security checks
export const validateEmail = (email) => {
  const errors = [];
  
  if (!email || typeof email !== 'string') {
    errors.push('Email is required');
    return { isValid: false, errors };
  }

  // Basic format validation
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(email)) {
    errors.push('Please enter a valid email address');
  }

  // Length validation
  if (email.length > 254) {
    errors.push('Email address is too long');
  }

  // Check for suspicious patterns
  const suspiciousPatterns = [
    /script/i,
    /<.*>/,
    /javascript:/i,
    /on\w+=/i,
    /\.\./,
    /[<>\"']/
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(email)) {
      errors.push('Email contains invalid characters');
      break;
    }
  }

  // Disposable email domains (basic list)
  const disposableDomains = [
    '10minutemail.com',
    'guerrillamail.com',
    'mailinator.com',
    'temp-mail.org',
    'throwaway.email',
    'yopmail.com'
  ];

  const domain = email.split('@')[1]?.toLowerCase();
  if (domain && disposableDomains.includes(domain)) {
    errors.push('Please use a permanent email address');
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: sanitizeString(email.toLowerCase().trim())
  };
};

// Phone validation with international support
export const validatePhone = (phone, countryCode = '+44') => {
  const errors = [];
  
  if (!phone || typeof phone !== 'string') {
    errors.push('Phone number is required');
    return { isValid: false, errors };
  }

  // Remove all non-digit characters for validation
  const digitsOnly = phone.replace(/\D/g, '');
  
  if (digitsOnly.length === 0) {
    errors.push('Please enter a valid phone number');
    return { isValid: false, errors };
  }

  // Country-specific validation
  const phoneValidation = {
    '+30': { min: 10, max: 10, name: 'Greece' }, // Greek numbers
    '+44': { min: 10, max: 11, name: 'UK' },
    '+1': { min: 10, max: 10, name: 'US/Canada' },
    '+49': { min: 10, max: 12, name: 'Germany' },
    '+33': { min: 9, max: 10, name: 'France' },
    '+39': { min: 9, max: 11, name: 'Italy' },
    '+31': { min: 9, max: 9, name: 'Netherlands' },
    '+7': { min: 10, max: 10, name: 'Russia' }
  };

  const validation = phoneValidation[countryCode];
  if (!validation) {
    // Default validation for unknown country codes
    if (digitsOnly.length < 7 || digitsOnly.length > 15) {
      errors.push('Phone number length is invalid for the selected country');
    }
  } else {
    if (digitsOnly.length < validation.min || digitsOnly.length > validation.max) {
      errors.push(`Please enter a valid ${validation.name} phone number`);
    }
  }

  // Check for suspicious patterns
  if (/^(.)\1{6,}$/.test(digitsOnly)) {
    errors.push('Phone number appears to be invalid (too many repeated digits)');
  }

  // Sanitize the phone number
  const sanitizedPhone = phone.replace(/[^\d\s\-\+\(\)]/g, '').trim();

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: sanitizedPhone,
    formatted: `${countryCode} ${digitsOnly}`
  };
};

// Name validation with security checks
export const validateName = (name) => {
  const errors = [];
  
  if (!name || typeof name !== 'string') {
    errors.push('Name is required');
    return { isValid: false, errors };
  }

  const trimmedName = name.trim();
  
  if (trimmedName.length < 2) {
    errors.push('Name must be at least 2 characters long');
  }

  if (trimmedName.length > 100) {
    errors.push('Name is too long');
  }

  // Allow letters, spaces, hyphens, apostrophes, and some international characters
  const nameRegex = /^[a-zA-ZÀ-ÿĀ-žА-я\s\-'\.]+$/;
  if (!nameRegex.test(trimmedName)) {
    errors.push('Name contains invalid characters');
  }

  // Check for suspicious patterns
  const suspiciousPatterns = [
    /script/i,
    /<.*>/,
    /javascript:/i,
    /on\w+=/i,
    /\{.*\}/,
    /\[.*\]/
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(trimmedName)) {
      errors.push('Name contains invalid characters');
      break;
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: sanitizeString(trimmedName)
  };
};

// Date/time validation
export const validateDateTime = (datetime) => {
  const errors = [];
  
  if (!datetime || !(datetime instanceof Date)) {
    errors.push('Please select a valid date and time');
    return { isValid: false, errors };
  }

  const now = new Date();
  const oneYearFromNow = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);

  if (datetime > oneYearFromNow) {
    errors.push('Bookings cannot be made more than one year in advance');
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: datetime
  };
};

// Number validation (passengers, luggage)
export const validateNumber = (value, min = 0, max = 50, fieldName = 'Value') => {
  const errors = [];
  
  const num = parseInt(value, 10);
  
  if (isNaN(num)) {
    errors.push(`${fieldName} must be a valid number`);
    return { isValid: false, errors };
  }

  if (num < min) {
    errors.push(`${fieldName} must be at least ${min}`);
  }

  if (num > max) {
    errors.push(`${fieldName} cannot exceed ${max}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: num
  };
};

// Location validation (ensure within Corfu bounds)
export const validateLocation = (location, locationType = 'location') => {
  const errors = [];
  
  if (!location || !location.position) {
    errors.push(`Please select a valid ${locationType}`);
    return { isValid: false, errors };
  }

  const { lat, lng } = location.position;
  
  // Corfu bounds with small buffer
  const CORFU_BOUNDS = {
    north: 39.850,
    south: 39.260,
    east: 20.140,
    west: 19.570
  };

  if (lat < CORFU_BOUNDS.south || lat > CORFU_BOUNDS.north || 
      lng < CORFU_BOUNDS.west || lng > CORFU_BOUNDS.east) {
    errors.push(`${locationType} must be within Corfu island`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized: {
      name: sanitizeString(location.name || ''),
      position: {
        lat: parseFloat(lat),
        lng: parseFloat(lng)
      }
    }
  };
};

// Generic string sanitization
export const sanitizeString = (str) => {
  if (typeof str !== 'string') return '';
  
  return str
    .trim()
    .replace(/[<>\"'&]/g, '') // Remove potentially dangerous characters
    .replace(/\s+/g, ' ') // Normalize whitespace
    .substring(0, 1000); // Limit length
};

// Comprehensive booking validation
export const validateBookingForm = (formData) => {
  const errors = [];
  const sanitizedData = {};

  // Validate name
  const nameValidation = validateName(formData.fullName);
  if (!nameValidation.isValid) {
    errors.push(...nameValidation.errors);
  } else {
    sanitizedData.fullName = nameValidation.sanitized;
  }

  // Validate email
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.push(...emailValidation.errors);
  } else {
    sanitizedData.email = emailValidation.sanitized;
  }

  // Validate phone
  const phoneValidation = validatePhone(formData.phone, formData.countryCode);
  if (!phoneValidation.isValid) {
    errors.push(...phoneValidation.errors);
  } else {
    sanitizedData.phone = phoneValidation.formatted;
  }

  // Validate passengers
  const passengerValidation = validateNumber(formData.passengers, 1, 21, 'Number of passengers');
  if (!passengerValidation.isValid) {
    errors.push(...passengerValidation.errors);
  } else {
    sanitizedData.passengers = passengerValidation.sanitized;
  }

  // Validate luggage - limit based on number of vans needed (8 pieces per van)
  const vansNeeded = Math.ceil((formData.passengers || 1) / 7);
  const maxLuggage = vansNeeded * 8;
  const luggageValidation = validateNumber(formData.luggage, 0, maxLuggage, 'Number of luggage items');
  if (!luggageValidation.isValid) {
    errors.push(...luggageValidation.errors);
  } else {
    sanitizedData.luggage = luggageValidation.sanitized;
  }

  // Validate datetime
  const datetimeValidation = validateDateTime(formData.datetime);
  if (!datetimeValidation.isValid) {
    errors.push(...datetimeValidation.errors);
  } else {
    sanitizedData.datetime = datetimeValidation.sanitized;
  }

  // Validate pickup location
  const pickupValidation = validateLocation(formData.pickup, 'pickup location');
  if (!pickupValidation.isValid) {
    errors.push(...pickupValidation.errors);
  } else {
    sanitizedData.pickup = pickupValidation.sanitized;
  }

  // Validate dropoff location
  const dropoffValidation = validateLocation(formData.dropoff, 'dropoff location');
  if (!dropoffValidation.isValid) {
    errors.push(...dropoffValidation.errors);
  } else {
    sanitizedData.dropoff = dropoffValidation.sanitized;
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedData
  };
};

// Rate limiting helper (client-side tracking)
export const checkRateLimit = (action, limit = 3, timeWindow = 60000) => {
  const key = `rate_limit_${action}`;
  const now = Date.now();
  
  let attempts = JSON.parse(localStorage.getItem(key) || '[]');
  
  // Remove old attempts outside the time window
  attempts = attempts.filter(timestamp => now - timestamp < timeWindow);
  
  if (attempts.length >= limit) {
    return {
      allowed: false,
      message: `Too many attempts. Please wait ${Math.ceil(timeWindow / 60000)} minute(s) before trying again.`
    };
  }
  
  // Add current attempt
  attempts.push(now);
  localStorage.setItem(key, JSON.stringify(attempts));
  
  return { allowed: true };
}; 